import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { sendDoiEmail, sendWelcomeEmail } from '@/lib/edge-functions'
import { getTenantConfig } from '@/lib/tenant-config'
import {
  Button,
  Input,
  Label,
  Checkbox,
  Card,
  CardContent,
  Skeleton,
} from '@lead-magnifier/ui'
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react'
import confetti from 'canvas-confetti'

export const Route = createFileRoute('/form/$tenantSlug/$eventSlug/')({
  component: LeadFormPage,
})

interface EventData {
  id: string
  name: string
  slug: string
  logoUrl: string | null
  headerImageUrl: string | null
  primaryColor: string
  backgroundColor: string
  introText: string | null
  downloadUrl: string
  ctaButtonText: string
  doubleOptIn: boolean
  nameRequired: boolean
  customFields: Array<{
    name: string
    type: 'text' | 'select' | 'checkbox'
    options?: string[]
    required: boolean
  }>
  privacyText: string
  privacyUrl: string | null
  submitButtonText: string
  tenantId: string
}

type FormState = 'loading' | 'form' | 'submitting' | 'success' | 'confirm-email' | 'error'

function LeadFormPage() {
  const { tenantSlug, eventSlug } = Route.useParams()
  const [state, setState] = useState<FormState>('loading')
  const [event, setEvent] = useState<EventData | null>(null)
  const [error, setError] = useState('')

  // Form fields
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({})

  useEffect(() => {
    loadEvent()
  }, [tenantSlug, eventSlug])

  const loadEvent = async () => {
    try {
      // First get tenant by slug
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', tenantSlug)
        .single()

      if (tenantError || !tenant) {
        setError('Event nicht gefunden')
        setState('error')
        return
      }

      // Then get event
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('slug', eventSlug)
        .eq('status', 'active')
        .single()

      if (eventError || !eventData) {
        setError('Event nicht gefunden oder nicht aktiv')
        setState('error')
        return
      }

      setEvent({
        id: eventData.id,
        name: eventData.name,
        slug: eventData.slug,
        logoUrl: eventData.logo_url,
        headerImageUrl: eventData.header_image_url,
        primaryColor: eventData.primary_color || '#3b82f6',
        backgroundColor: eventData.background_color || '#007E87',
        introText: eventData.intro_text,
        downloadUrl: eventData.download_url,
        ctaButtonText: eventData.cta_button_text || 'Jetzt herunterladen',
        doubleOptIn: eventData.double_opt_in,
        nameRequired: eventData.name_required,
        customFields: (eventData.custom_fields as EventData['customFields']) || [],
        privacyText: eventData.privacy_text || 'Ich stimme der Datenschutzerklärung zu.',
        privacyUrl: eventData.privacy_url,
        submitButtonText: eventData.submit_button_text || 'Absenden',
        tenantId: tenant.id,
      })
      setState('form')
    } catch (err) {
      console.error('Error loading event:', err)
      setError('Fehler beim Laden des Events')
      setState('error')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!event || !consent) return

    setState('submitting')

    try {
      // Generate DOI token if double opt-in is enabled
      const doiToken = event.doubleOptIn ? crypto.randomUUID() : null

      // Insert lead
      const { data: lead, error: insertError } = await supabase
        .from('leads')
        .insert({
          tenant_id: event.tenantId,
          event_id: event.id,
          email,
          name: name || null,
          consent: true,
          doi_token: doiToken,
          custom_fields: customFieldValues,
          ip_address: null, // Would need server-side for this
          user_agent: navigator.userAgent,
          source: 'form',
        })
        .select()
        .single()

      if (insertError) {
        // Check for duplicate
        if (insertError.code === '23505') {
          setError('Diese E-Mail ist bereits registriert.')
          setState('form')
          return
        }
        throw insertError
      }

      // Record activity
      await supabase.from('lead_activities').insert({
        lead_id: lead.id,
        activity_type: 'registered',
        metadata: { source: 'form' },
      })

      // Send email via Edge Function
      if (event.doubleOptIn && doiToken) {
        const confirmationUrl = `${window.location.origin}/confirm/${doiToken}`
        try {
          await sendDoiEmail(lead.id, event.id, email, confirmationUrl)
        } catch (emailError) {
          console.error('Failed to send DOI email:', emailError)
          // Continue anyway - lead is saved
        }
        setState('confirm-email')
      } else {
        // No DOI required - send welcome email directly
        try {
          await sendWelcomeEmail(lead.id, event.id, email)
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError)
        }
        // Direct success - show confetti
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        })
        setState('success')
      }
    } catch (err) {
      console.error('Submit error:', err)
      setError('Fehler beim Absenden. Bitte versuche es erneut.')
      setState('form')
    }
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 space-y-6">
            <Skeleton className="h-16 w-16 rounded-full mx-auto" />
            <Skeleton className="h-8 w-3/4 mx-auto" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Oops!</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Email confirmation state (DOI)
  if (state === 'confirm-email') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(135deg, ${event?.backgroundColor || '#007E87'} 0%, ${event?.primaryColor || '#3b82f6'} 100%)`,
        }}
      >
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Fast geschafft!</h1>
            <p className="text-muted-foreground mb-6">
              Wir haben dir eine E-Mail an <strong>{email}</strong> gesendet.
              Klicke auf den Link in der E-Mail, um deine Anmeldung zu bestätigen.
            </p>
            <p className="text-sm text-muted-foreground">
              Keine E-Mail erhalten? Überprüfe deinen Spam-Ordner.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Success state
  if (state === 'success') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(135deg, ${event?.backgroundColor || '#007E87'} 0%, ${event?.primaryColor || '#3b82f6'} 100%)`,
        }}
      >
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Vielen Dank!</h1>
            <p className="text-muted-foreground mb-6">
              Deine Anmeldung war erfolgreich.
            </p>
            {event?.downloadUrl && (
              <Button asChild className="w-full" size="lg">
                <a href={event.downloadUrl} target="_blank" rel="noopener noreferrer">
                  {event.ctaButtonText}
                </a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // Form state
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${event?.backgroundColor || '#007E87'} 0%, ${event?.primaryColor || '#3b82f6'} 100%)`,
      }}
    >
      <Card className="w-full max-w-md shadow-2xl">
        {/* Header Image */}
        {event?.headerImageUrl && (
          <div className="h-32 overflow-hidden rounded-t-lg">
            <img
              src={event.headerImageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <CardContent className="p-6 space-y-6">
          {/* Logo */}
          {event?.logoUrl && (
            <div className="flex justify-center -mt-12 relative z-10">
              <img
                src={event.logoUrl}
                alt={event?.name}
                className="h-20 w-20 rounded-full bg-white shadow-lg object-contain p-2"
              />
            </div>
          )}

          {/* Title */}
          <div className="text-center">
            <h1 className="text-2xl font-bold">{event?.name}</h1>
            {event?.introText && (
              <p className="text-muted-foreground mt-2">{event.introText}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Name field */}
            {(event?.nameRequired || true) && (
              <div className="space-y-2">
                <Label htmlFor="name">
                  Name {event?.nameRequired && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Max Mustermann"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={event?.nameRequired}
                />
              </div>
            )}

            {/* Email field */}
            <div className="space-y-2">
              <Label htmlFor="email">
                E-Mail <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@beispiel.at"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Custom fields */}
            {event?.customFields?.map((field, index) => (
              <div key={index} className="space-y-2">
                <Label htmlFor={`custom-${index}`}>
                  {field.name} {field.required && <span className="text-destructive">*</span>}
                </Label>
                {field.type === 'select' && field.options ? (
                  <select
                    id={`custom-${index}`}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    value={customFieldValues[field.name] || ''}
                    onChange={(e) =>
                      setCustomFieldValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    required={field.required}
                  >
                    <option value="">Bitte wählen...</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={`custom-${index}`}
                    type="text"
                    value={customFieldValues[field.name] || ''}
                    onChange={(e) =>
                      setCustomFieldValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    required={field.required}
                  />
                )}
              </div>
            ))}

            {/* Consent checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked === true)}
                required
              />
              <label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                {event?.privacyUrl ? (
                  <>
                    {event.privacyText.split('Datenschutzerklärung')[0]}
                    <a
                      href={event.privacyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Datenschutzerklärung
                    </a>
                    {event.privacyText.split('Datenschutzerklärung')[1]}
                  </>
                ) : (
                  event?.privacyText
                )}
              </label>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={!consent || state === 'submitting'}
              style={{ backgroundColor: event?.primaryColor }}
            >
              {state === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                event?.submitButtonText
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            Powered by {getTenantConfig().branding.name}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
